/**
 * Spoken distances.
 *
 * The whole value of voice on a buggy is that it works without looking down.
 * The whole risk is that it becomes chatter and gets muted on the first hole,
 * after which it may as well not exist. So the rule is deliberately stingy:
 *
 *   SPEAK WHEN YOU STOP. You drive, you park, you want the number — that is
 *   exactly the moment you would otherwise look at the screen. Never while
 *   moving, and never on a timer.
 *
 *   SPEAK ONCE PER HOLE ARRIVAL. "Hole 3, par 4, 380 metres" as you reach the
 *   tee, and not again.
 *
 *   ALWAYS SPEAK A KEEP-OFF WARNING. That one is not chatter; it is the reason
 *   the fence exists, and the driver is looking out the front, not at a banner.
 *
 * Uses the browser's own speech synthesis: no API key, no network, works in the
 * dead spots where a cloud voice would fail — which is precisely where a golfer
 * is least able to check their phone.
 */

export type Announcement =
  | { kind: 'hole'; text: string; hole: number }
  | { kind: 'distance'; text: string }
  | { kind: 'warning'; text: string };

export interface VoiceState {
  /** Hole we have already announced. */
  spokenHole: number | null;
  /** Were we moving on the previous fix. */
  wasMoving: boolean;
  /** When we last read a distance, so stopping twice in a row stays quiet. */
  lastDistanceAt: number;
  /** Last warning spoken, so a fence does not repeat while parked on it. */
  lastWarning: string | null;
}

export const initialVoiceState = (): VoiceState => ({
  spokenHole: null,
  wasMoving: false,
  lastDistanceAt: 0,
  lastWarning: null,
});

/** Above this, the buggy is moving. Matches the heading logic. */
const MOVING_MS = 0.7;
/** Minimum gap between two spoken distances. */
const DISTANCE_COOLDOWN_MS = 25_000;

export interface VoiceInput {
  hole: number;
  par: number;
  holeLength: number;
  distanceToGreen: number;
  unit: 'm' | 'yd';
  speed: number;
  breach: string | null;
  now: number;
  /** No point talking about a distance derived from a rubbish fix. */
  accuracy: number;
}

const unitWord = (u: 'm' | 'yd') => (u === 'yd' ? 'yards' : 'metres');

/**
 * Decide what, if anything, to say. Pure, so the policy can be tested by
 * replaying a round rather than by driving one.
 */
export function decide(
  state: VoiceState,
  input: VoiceInput
): { state: VoiceState; say: Announcement | null } {
  const { hole, par, holeLength, distanceToGreen, unit, speed, breach, now, accuracy } = input;

  // Warnings outrank everything and ignore the cooldowns.
  if (breach && breach !== state.lastWarning) {
    return {
      state: { ...state, lastWarning: breach, wasMoving: speed >= MOVING_MS },
      say: { kind: 'warning', text: `Keep off. ${breach}.` },
    };
  }
  const clearedWarning = breach ? state.lastWarning : null;

  const moving = speed >= MOVING_MS;

  // Arriving at a new hole.
  if (hole !== state.spokenHole) {
    return {
      state: { ...state, spokenHole: hole, wasMoving: moving, lastWarning: clearedWarning },
      say: {
        kind: 'hole',
        hole,
        text: `Hole ${hole}. Par ${par}. ${Math.round(holeLength)} ${unitWord(unit)}.`,
      },
    };
  }

  // The moment of stopping — and only if the fix is good enough to trust.
  const justStopped = state.wasMoving && !moving;
  const cooledDown = now - state.lastDistanceAt > DISTANCE_COOLDOWN_MS;
  if (justStopped && cooledDown && accuracy <= 25) {
    return {
      state: { ...state, wasMoving: moving, lastDistanceAt: now, lastWarning: clearedWarning },
      say: {
        kind: 'distance',
        text: `${Math.round(distanceToGreen)} ${unitWord(unit)} to the middle.`,
      },
    };
  }

  return { state: { ...state, wasMoving: moving, lastWarning: clearedWarning }, say: null };
}

// --- speaking ---------------------------------------------------------------

let unlocked = false;

export const voiceSupported = () =>
  typeof window !== 'undefined' && 'speechSynthesis' in window;

/**
 * iOS refuses to speak until synthesis has been triggered by a real tap, so the
 * first utterance has to be primed from a user gesture.
 */
export function unlockVoice() {
  if (!voiceSupported() || unlocked) return;
  try {
    const u = new SpeechSynthesisUtterance('');
    u.volume = 0;
    window.speechSynthesis.speak(u);
    unlocked = true;
  } catch {
    /* not fatal — the app is fully usable silent */
  }
}

export function speak(a: Announcement) {
  if (!voiceSupported()) return;
  try {
    // A warning must not queue behind a hole announcement.
    if (a.kind === 'warning') window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(a.text);
    // Slightly slow and loud: this is competing with wind and a buggy motor.
    u.rate = a.kind === 'warning' ? 1.05 : 0.95;
    u.volume = 1;
    u.pitch = 1;
    u.lang = 'en-AU';
    window.speechSynthesis.speak(u);
  } catch {
    /* ignore — silence is an acceptable degradation */
  }
}

export function stopSpeaking() {
  if (voiceSupported()) {
    try {
      window.speechSynthesis.cancel();
    } catch {
      /* nothing to do */
    }
  }
}
